import React, { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE = 2 * 60 * 1000; // Warning 2 minutes before

const InactivityGuard: React.FC = () => {
  const { logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const countdownRef = useRef<ReturnType<typeof setInterval>>();

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);

    if (showWarning) {
      setShowWarning(false);
      setCountdown(120);
    }

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(120);
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);

    timerRef.current = setTimeout(() => {
      // End session before logout
      import('../services/authService').then(m => m.endSession("inactivity")).catch(() => {});
      logout();
      window.location.href = "/login";
    }, INACTIVITY_TIMEOUT);
  }, [logout, showWarning]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keypress", "scroll", "touchstart", "click"];
    events.forEach((evt) => document.addEventListener(evt, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, resetTimer));
      clearTimeout(timerRef.current);
      clearTimeout(warningTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [resetTimer]);

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-2xl">
        <div className="text-5xl mb-4">&#9201;</div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">
          Session about to expire
        </h3>
        <p className="text-slate-500 mb-6">
          You will be logged out in{" "}
          <strong className="text-red-600 text-lg">{countdown}</strong>{" "}
          seconds due to inactivity.
        </p>
        <button
          onClick={resetTimer}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-3 rounded-xl transition-colors duration-200"
        >
          I am here
        </button>
      </div>
    </div>
  );
};

export default InactivityGuard;
